import {
    ApolloLink,
    ApolloClient,
    HttpLink,
    HttpOptions,
    InMemoryCache,
    from
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import fetch from 'isomorphic-unfetch';
import { IncomingHttpHeaders } from 'http';
import { logError, logInfo } from '@zoopla/logger';

let apolloClientInstance: ApolloClient<unknown> | null = null;

const CONTENTFUL_DELIVERY_TOKEN = process.env.CONTENTFUL_DELIVERY_TOKEN;
const CONTENTFUL_PREVIEW_TOKEN = process.env.CONTENTFUL_PREVIEW_TOKEN;
const CONTENTFUL_GRAPHQL_URL = `${
    process.env.CONTENTFUL_GRAPHQL_URL! + process.env.CONTENTFUL_SPACE_ID!
}/environments/${process.env.CONTENTFUL_ENVIRONMENT_ID}`;

const previewHostRegex = new RegExp(/^preview\.(?:\w+\.)?zoopla.co.uk$/);

let fetchOptions: HttpOptions;
if (typeof window === 'undefined') {
    // On the server, create an agentkeepalive client
    // so that we reuse socket connections to the GraphQL API endpoint
    // See: https://www.npmjs.com/package/agentkeepalive
    // And: https://www.lob.com/blog/use-http-keep-alive
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const HttpsAgent = require('agentkeepalive').HttpsAgent;
    fetchOptions = { agent: new HttpsAgent() } as HttpOptions;
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const PREFIX = 'GraphQL';

    let messages: string[] = [];

    if (graphQLErrors) {
        messages = messages.concat(
            graphQLErrors
                .filter(
                    ({ path, message }) =>
                        // dont log known listings validation errors
                        !(
                            operation.operationName === 'getListingData' &&
                            path &&
                            path[0].toString() === 'searchResults' &&
                            message.startsWith('422:')
                        ) &&
                        // dont log expired listing errors
                        !(
                            operation.operationName === 'getListingDetails' &&
                            path &&
                            path[0].toString() === 'listing' &&
                            message === 'Listing Expired'
                        ) &&
                        // do not log non-existing Contentful entry id's errors
                        !(
                            operation.operationName === 'getContentTypeById' &&
                            path &&
                            [
                                'basicTable',
                                'imageWrapper',
                                'button',
                                'htmlWrapper'
                            ].includes(path[0].toString()) &&
                            message ===
                                'An unexpected error happened on our side.'
                        )
                )
                .map(
                    ({ message, path }) =>
                        `[${PREFIX}] [graphQLError]: Operation: ${operation.operationName}, Message: ${message}, Path: ${path}`
                )
        );
    }

    if (networkError) {
        messages.push(
            `[${PREFIX}] [networkError]: Operation: ${operation.operationName}, Message: ${networkError}`
        );
    }

    messages.forEach((message) =>
        process.env.NODE_ENV === 'test' ? logInfo(message) : logError(message)
    );
});

const createApolloClient = (
    initialState = {},
    headers?: IncomingHttpHeaders
) => {
    //GraphQL Lambda connection
    const gqlLambda = new HttpLink({
        uri: process.env.NEXT_PUBLIC_GRAPHQL_API,
        credentials: 'include',
        fetch,
        fetchOptions,
        headers: headers
            ? {
                  cookie: headers.cookie,
                  'X-Api-Key': process.env.NEXT_PUBLIC_API_KEY
              }
            : { 'X-Api-Key': process.env.NEXT_PUBLIC_API_KEY }
    });

    //Contentful preview mode
    let contentfulPreviewMode = false;
    if (headers && headers['x-zoopla-preview-domain'] === 'true') {
        if (previewHostRegex.test(headers['x-forwarded-host'] as string)) {
            contentfulPreviewMode = true;
        }
    }

    //Contentful authorization token
    const contentfulAuthorizationToken = contentfulPreviewMode
        ? CONTENTFUL_PREVIEW_TOKEN
        : CONTENTFUL_DELIVERY_TOKEN;

    //Contentful connection
    const gqlContentFul = new HttpLink({
        uri: CONTENTFUL_GRAPHQL_URL,
        fetch,
        headers: {
            authorization: `Bearer ${contentfulAuthorizationToken}`,
            'Content-Language': 'en-us'
        }
    });

    //Contentful middleware link
    const contentfulMiddlewareLink = new ApolloLink((operation, forward) => {
        // In preview mode we append a preview parameter to graphql variables.
        // When combined with using preview token Contentful will return
        // unpublished content.
        if (contentfulPreviewMode) {
            operation.variables.preview = contentfulPreviewMode;
        }
        return forward(operation);
    });

    const contentfulLink = contentfulMiddlewareLink.concat(gqlContentFul);

    const link = from([
        errorLink,
        ApolloLink.split(
            (operation) => operation.getContext().clientName === 'contentful',
            contentfulLink,
            gqlLambda
        )
    ]);

    return new ApolloClient({
        link,
        ssrMode: typeof window === 'undefined',
        cache: new InMemoryCache({
            typePolicies: {
                User: {
                    keyFields: ['userId']
                },
                Property: {
                    keyFields: ['propertyId', 'uprn']
                }
            }
        }).restore(initialState)
    });
};

const getApolloClient = (initialState = {}, headers?: IncomingHttpHeaders) => {
    // Reuse client on the client-side if available
    if (!apolloClientInstance) {
        apolloClientInstance = createApolloClient(initialState, headers);
    }

    return apolloClientInstance;
};

export { createApolloClient, getApolloClient, apolloClientInstance };
